<template>
  <!-- Advertisement List -->
  <div
    ref="list"
  >
    <Stack
      ref="feed"
      :class="{ 'animate-reflow': doAnimateReflow }"
      :column-min-width="smallAds ? 200 : 320"
      :gutter-width="smallAds ? 8 : 15"
      :gutter-height=" 15"
      monitor-images-loaded
    >
      <!-- Z index is so that the dropdowns dont overlap -->
      <StackItem
        v-for="(advertisement, index) in advertisements"
        :key="`${advertisement.ad_id}${index}`"
        :style="{ zIndex: advertisements.length - index }"
      >
        <BaseDynamicLoader
          v-slot="{ handleMediaLoaded }"
          :identifier="`${advertisement.ad_id}${index}`"
          :observer-elm-selector="observerElem"
          :buffer="500"
          enable-media-load-buffer
        >
          <LibraryCard
            :advertisement="advertisement"
            :discovery="discovery"
            :shared-board="sharedBoard"
            :shared-brief="sharedBrief"
            :no-drawer="noDrawer"
            :small-ads="smallAds"
            :hide-creative-count="hideCreativeCount"
            :edit-brief-view="editBriefView"
            :ad-details-saved-id="savedAdIds.find(ad => ad.uniqueAdId === advertisement.id)?.newAdId || ''"
            :show-spyder-ad-rank="showSpyderAdRank"
            :parent-partner-brand="parentPartnerBrand"
            @promptSignup="promptSignup = true"
            @loaded-content="() => { reflowGrid(); handleMediaLoaded(); }"
            @openDetails="handleOpenAdvertisementDetailsDrawer(advertisement, $event)"
            @adSaved="handleAdSaved(advertisement.id, $event)"
            @updateAds="$emit('updateAds', $event)"
          />
        </BaseDynamicLoader>
      </StackItem>
    </Stack>

    <!-- Advertisement Details Drawer -->
    <AdvertisementDetailsDrawer
      v-if="showAdvertisementDetailsDrawer && !trialExpired && !disableDetailsDrawer"
      :all-ads.sync="advertisements"
      :discovery="discovery"
      :advertisement="selectedAdvertisement"
      :shared-board="sharedBoard"
      :shared-brief="sharedBrief"
      :loading="loading"
      :ad-card-saved-id="savedAdIds.find(ad => ad.uniqueAdId === selectedAdvertisement.id)?.newAdId || ''"
      :all-ads-loaded="allAdsLoaded"
      :with-ad-copy-expanded="adDetailsCopyExpanded"
      @adSaved="handleAdSaved(selectedAdvertisement.id, $event)"
      @updateAds="$emit('updateAds', $event)"
      @removeAds="$emit('removeAds', $event)"
      @getMoreAds="$emit('getMoreAds')"
      @close="closeModal"
      @save="$emit('reload')"
    />

    <SignUpModal
      v-if="promptSignup"
      @close="promptSignup = false"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import { Stack, StackItem } from 'vue-stack-grid'
import AdvertisementDetailsDrawer from './AdvertisementDetailsDrawer'
import API from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import EventBus from '@/plugins/BaseNotifications/EventBus'
import LibraryCard from '../library/LibraryCard'
import SignUpModal from '../account/SignUpModal.vue'

export default {
  name: 'AdvertisementList',
  components: {
    LibraryCard,
    AdvertisementDetailsDrawer,
    Stack,
    StackItem,
    SignUpModal
  },
  props: {
    // Advertisements
    advertisements: {
      type: Array,
      default: () => []
    },
    sharedBoard: {
      type: Boolean,
      default: () => false
    },
    sharedBrief: {
      type: Boolean,
      default: () => false
    },
    discovery: {
      type: Boolean,
      default: () => false
    },
    editBriefView: {
      type: Boolean,
      default: () => false
    },
    noDrawer: {
      type: Boolean,
      default: () => false
    },
    smallAds: {
      type: Boolean,
      default: () => false
    },
    // Hide the per-card "same creative / duplicates" count chip.
    hideCreativeCount: {
      type: Boolean,
      default: () => false
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    allAdsLoaded: {
      type: Boolean,
      default: () => false
    },
    observerElem: {
      type: String,
      default: () => '.main-layout'
    },
    disableDetailsDrawer: {
      type: Boolean,
      default: () => false
    },
    showSpyderAdRank: {
      type: Boolean,
      default: () => false
    },
    // For partner ads, this is the object for the main brand that the partner ad is associated with
    parentPartnerBrand: {
      default: () => null
    }
  },
  data () {
    return {
      selectedAdvertisement: {},
      showAdvertisementDetailsDrawer: false,
      promptSignup: false,
      currentSize: 0,
      savedAdIds: [],
      adDetailsCopyExpanded: false,

      doAnimateReflow: false,
      animReflowTimeout: null
    }
  },
  computed: {
    ...mapState('AdvertisementsModule', ['adToOpen']),
    ...mapGetters('AuthModule', ['getUserFreeTrialDate', 'isFreeTier', 'getTeam']),
    trialExpired () {
      // check if a week has passed since trial and if they're free tier
      return this.isFreeTier && Date.now() > this.getUserFreeTrialDate + 6.048e8 && !this.getTeam && !this.discovery
    }
  },
  watch: {
    adToOpen (newValue) {
      if (Object.keys(newValue).length) {
        this.selectedAdvertisement = newValue
        this.showAdvertisementDetailsDrawer = true
      }
    }
  },
  async mounted () {
    EventBus.$on('sidebar:collapse-transition-end', this.handleSidebarCollapseTransitionEnd)

    const adId = this.$route.query.ad
    if (!adId) return

    let newAd = null
    try {
      const adFn = this.discovery ? ForeplayAPI.Ads.getUniqueAdById : API.Advertisements.get
      newAd = await adFn(adId)
    } catch (e) {
      this.$showAlert({
        type: 'error',
        message: 'Failed to load ad'
      })
    }
    if (Object.keys(newAd).length) {
      this.selectedAdvertisement = newAd
      this.showAdvertisementDetailsDrawer = true
    }
  },
  beforeDestroy () {
    EventBus.$off('sidebar:collapse-transition-end', this.handleSidebarCollapseTransitionEnd)
  },
  methods: {
    ...mapMutations('AdvertisementsModule', ['SET_AD_TO_OPEN']),
    handleOpenAdvertisementDetailsDrawer (advertisement, withCopyExpanded) {
      if (this.disableDetailsDrawer) {
        // In this case, we are handling the details drawer rendering somewhere else
        this.SET_AD_TO_OPEN(advertisement)
        return
      }
      this.adDetailsCopyExpanded = withCopyExpanded
      this.selectedAdvertisement = advertisement
      this.showAdvertisementDetailsDrawer = true
    },
    closeModal () {
      this.showAdvertisementDetailsDrawer = false
      this.selectedAdvertisement = {}
      this.SET_AD_TO_OPEN({})
      // idk why this is needed, but ima ignore it on discovery
      if (this.$route.query.ad && !this.discovery) this.$router.push({ name: 'LibraryView' })
    },
    reflowGrid () {
      this.$refs.feed.reflow()
    },
    handleAdSaved (uniqueAdId, newAdId) {
      this.savedAdIds.push({ uniqueAdId, newAdId })
    },
    handleSidebarCollapseTransitionEnd () {
      // Check if the user has deeply scrolled down the page
      // (See how much of the stack grid is overflowing the top of the viewport)
      const stackEl = this.$refs.feed?.$el
      if (stackEl) {
        const rect = stackEl.getBoundingClientRect()
        const overflowTop = Math.abs(Math.min(rect.top, 0))
        const threshold = window.innerHeight * 4 // Block transitions once the user has scrolled 6x viewport height
        if (overflowTop > threshold) {
          clearTimeout(this.animReflowTimeout)
          return
        }
      }

      // If the conditions are met, add the animate-reflow class to the Stack component
      this.doAnimateReflow = true
      clearTimeout(this.animReflowTimeout)
      this.animReflowTimeout = setTimeout(() => {
        this.doAnimateReflow = false
      }, 200)
    }
  }
}
</script>

<style scoped>
/*
Animate the reflow of the stack grid under the following conditions:
- The sidebar expand/collapse transition has ended and the user has not deeply scrolled down the page
  ^ If these conditions are met, the animate-reflow class is added to the Stack component
- The StackItem is within the viewport (select for a conditional class from BaseDynamicLoader, content-in-view)
*/
.animate-reflow ::v-deep .vsg-stack-item:has(.content-in-view) {
  transition: transform 250ms ease-in-out;
}
</style>
